/// Pure-function pre-flight for the OAuth callback. The route component
/// (`pages/OAuthCallback.tsx`) is mostly side effects — react-router param
/// reading, navigation, fetch — so we keep the *decision* logic here where
/// it can be unit-tested without a DOM.
///
/// The classifier turns the (searchParams, sessionStorage-state) pair into
/// one of six tagged outcomes; the renderer then dispatches on the tag.

/// State format encoded by the server. Strings, not enums, so the value
/// the server actually emits can be matched directly.
const STATE_PREFIX_CONNECTION = 'connection';
const STATE_PLATFORM_DESKTOP = 'desktop';

export type CallbackPrecheck =
  | { kind: 'missing' }
  | { kind: 'no_session_state' }
  | { kind: 'state_mismatch' }
  | { kind: 'desktop'; code: string; state: string }
  | { kind: 'connection'; code: string; state: string }
  | { kind: 'link'; code: string; state: string }
  | { kind: 'reauth'; code: string; state: string }
  | { kind: 'ok'; code: string; state: string; source: string | undefined };

/// Classify the callback URL.
///
/// `consumeRememberedState` is the single-use accessor for the CSRF nonce
/// the panel put in sessionStorage when it kicked off OAuth. It's a
/// parameter rather than a direct import so tests can drive it without
/// needing a jsdom sessionStorage.
///
/// State formats:
///   account:{provider}:{platform}:{nonce}:{hmac}
///   connection:{provider}:{user}:{connection}:{nonce}:{hmac}
///
/// Two of these short-circuit the CSRF check on purpose:
///   - `connection:` callbacks are always native-app-initiated (the panel
///     has no connect UI), so the panel is just a relay.
///   - `account:{*}:desktop:…` callbacks are also app-initiated. The HMAC
///     in the state still binds them server-side when the app exchanges
///     the code — the panel doesn't need its own nonce.
export function precheckCallback(
  searchParams: URLSearchParams,
  consumeRememberedState: () => string | null,
): CallbackPrecheck {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  if (!code || !state) return { kind: 'missing' };

  const parts = state.split(':');

  if (parts[0] === STATE_PREFIX_CONNECTION) {
    return { kind: 'connection', code, state };
  }

  const platform = parts.length >= 3 ? parts[2] : undefined;
  if (platform === STATE_PLATFORM_DESKTOP) {
    return { kind: 'desktop', code, state };
  }

  const expected = consumeRememberedState();
  if (!expected) return { kind: 'no_session_state' };
  if (expected !== state) return { kind: 'state_mismatch' };

  if (parts[0] === 'link') return { kind: 'link', code, state };
  if (parts[0] === 'reauth') return { kind: 'reauth', code, state };

  const source = parts.length >= 2 ? parts[1] : undefined;
  return { kind: 'ok', code, state, source };
}

/// Map a precheck outcome (and an optional API error from a successful
/// `ok` path) to a user-facing error string, or `null` if there's nothing
/// to display. Desktop / connection relays render their own waiting copy,
/// not an error.
export function errorMessageFor(
  precheck: CallbackPrecheck,
  apiError: string,
): string | null {
  switch (precheck.kind) {
    case 'missing':
      return 'Missing OAuth parameters. Please try signing in again.';
    case 'no_session_state':
      return 'This OAuth callback has no matching sign-in attempt in this browser. Please try again.';
    case 'state_mismatch':
      return 'OAuth state mismatch. Please try signing in again.';
    case 'desktop':
      return null;
    case 'connection':
      return null;
    case 'link':
    case 'reauth':
      return apiError || null;
    case 'ok':
      return apiError || null;
  }
}
